import type {
  IdCardAudience, IdCardDesignInput, IdCardField, IdCardLayout,
} from '../../types/idCards';

/**
 * The ID card catalog: the layouts a design can use, the details each
 * audience's card can show and the colour schemes offered. Mirrors
 * backend/src/constants/idCards.ts (which validates designs).
 */

/** A CR80 card — the bank-card size every card printer takes — in points. */
export const CARD_LONG = 242.65;
export const CARD_SHORT = 153.07;

export interface LayoutDef {
  key: IdCardLayout;
  label: string;
  description: string;
  orientation: 'landscape' | 'portrait';
  /** Details the front has room for. */
  maxFields: number;
}

export const LAYOUTS: readonly LayoutDef[] = [
  { key: 'classic',  label: 'Classic',  description: 'Colour masthead, photo beside the details', orientation: 'landscape', maxFields: 4 },
  { key: 'sidebar',  label: 'Sidebar',  description: 'Photo on a colour panel, details beside it', orientation: 'landscape', maxFields: 5 },
  { key: 'portrait', label: 'Portrait', description: 'Vertical lanyard card, photo at the centre', orientation: 'portrait',  maxFields: 4 },
  { key: 'minimal',  label: 'Minimal',  description: 'White card, a single colour edge',           orientation: 'landscape', maxFields: 4 },
];

export const layoutOf = (key: IdCardLayout): LayoutDef => LAYOUTS.find(l => l.key === key) ?? LAYOUTS[0];

/** The card's width and height in points. */
export const cardSize = (layout: IdCardLayout): { width: number; height: number } =>
  layoutOf(layout).orientation === 'portrait'
    ? { width: CARD_SHORT, height: CARD_LONG }
    : { width: CARD_LONG, height: CARD_SHORT };

export interface FieldDef {
  key: IdCardField;
  label: string;
  /** Printed in the monospace face (numbers people read aloud or type in). */
  mono?: boolean;
  /** Takes a whole row of the details grid — an email has nowhere to wrap. */
  wide?: boolean;
}

export const FIELDS: { student: readonly FieldDef[]; staff: readonly FieldDef[] } = {
  student: [
    { key: 'grNumber', label: 'GR No.', mono: true },
    { key: 'classSection', label: 'Class' },
    { key: 'rollNumber', label: 'Roll No.', mono: true },
    { key: 'fatherName', label: 'Father' },
    { key: 'dob', label: 'Date of birth' },
    { key: 'phone', label: 'Guardian phone', mono: true },
    { key: 'session', label: 'Session' },
  ],
  staff: [
    { key: 'staffCode', label: 'Staff ID', mono: true },
    { key: 'designation', label: 'Designation' },
    { key: 'phone', label: 'Phone', mono: true },
    { key: 'email', label: 'Email', wide: true },
  ],
};

export const fieldDef = (audience: IdCardAudience, key: IdCardField): FieldDef | undefined =>
  FIELDS[audience].find(f => f.key === key);

export const AUDIENCE_LABEL: Record<IdCardAudience, string> = { student: 'Student', staff: 'Staff' };

/** Colour schemes that print well: a deep primary and a warm accent. */
export const PALETTES: ReadonlyArray<{ name: string; primary: string; accent: string }> = [
  { name: 'Royal navy',  primary: '#1B2F6B', accent: '#C9A227' },
  { name: 'Emerald',     primary: '#0E5A45', accent: '#D4AF37' },
  { name: 'Maroon',      primary: '#6E1A2A', accent: '#D9A441' },
  { name: 'Ocean',       primary: '#0B5368', accent: '#F2A541' },
  { name: 'Graphite',    primary: '#1F2937', accent: '#38BDF8' },
  { name: 'Cobalt',      primary: '#1D4ED8', accent: '#FBBF24' },
  { name: 'Plum',        primary: '#4A1D7A', accent: '#F5B942' },
  { name: 'Forest',      primary: '#1F4D2B', accent: '#A3C14A' },
];

export const MAX_INSTRUCTIONS = 4;
export const INSTRUCTION_MAX_LENGTH = 110;

/** A new design: the school's first card should already look finished. */
export const newDesign = (audience: IdCardAudience): IdCardDesignInput => ({
  name: audience === 'student' ? 'Student card' : 'Staff card',
  layout: audience === 'student' ? 'classic' : 'portrait',
  colors: { primary: PALETTES[0].primary, accent: PALETTES[0].accent },
  title: audience === 'student' ? 'Student Identity Card' : 'Staff Identity Card',
  fields: audience === 'student'
    ? ['grNumber', 'classSection', 'fatherName', 'phone']
    : ['staffCode', 'designation', 'phone'],
  back: {
    enabled: true,
    showSchoolContact: true,
    instructions: audience === 'student'
      ? [
        'This card is the property of the school and must be carried on campus at all times.',
        'It is not transferable. Report a lost card to the school office at once.',
      ]
      : [
        'Wear this card visibly while on campus.',
        'Report a lost card to the administration office. Return it when you leave the school.',
      ],
    signatory: 'Principal',
  },
});
